import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const TabRegistryContext = createContext(null);

export const TabRegistryProvider = ({ children }) => {
  // Registre des onglets : Map<tabId, { id, label, parent, level, children?, component? }>
  const [tabs, setTabs] = useState(new Map());

  // Enregistrer un onglet
  const registerTab = useCallback((tabData) => {
    setTabs(prev => {
      const newTabs = new Map(prev);
      newTabs.set(tabData.id, {
        id: tabData.id,
        label: tabData.label,
        parent: tabData.parent || null,
        level: tabData.level || 1,
        children: tabData.children || null,
        component: tabData.component || null,
        route: tabData.route || null
      });
      return newTabs;
    });
  }, []);

  // Enregistrer plusieurs onglets à la fois
  const registerTabs = useCallback((tabsArray) => {
    setTabs(prev => {
      const newTabs = new Map(prev);
      tabsArray.forEach(tabData => {
        newTabs.set(tabData.id, {
          id: tabData.id,
          label: tabData.label,
          parent: tabData.parent || null,
          level: tabData.level || 1,
          children: tabData.children || null,
          component: tabData.component || null,
          route: tabData.route || null
        });
      });
      return newTabs;
    });
  }, []);

  // Désenregistrer un onglet
  const unregisterTab = useCallback((tabId) => {
    setTabs(prev => {
      const newTabs = new Map(prev);
      newTabs.delete(tabId);
      return newTabs;
    });
  }, []);

  // Obtenir la hiérarchie complète des onglets
  const getTabsHierarchy = useCallback(() => {
    const tabsArray = Array.from(tabs.values());
    
    // Trier par niveau puis par label
    return tabsArray.sort((a, b) => {
      if (a.level !== b.level) {
        return a.level - b.level;
      }
      return a.label.localeCompare(b.label);
    });
  }, [tabs]);

  // Obtenir uniquement les onglets de niveau 1
  const getLevel1Tabs = useCallback(() => {
    return Array.from(tabs.values())
      .filter(tab => tab.level === 1)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tabs]);

  // Obtenir les enfants d'un parent
  const getChildren = useCallback((parentId) => {
    return Array.from(tabs.values())
      .filter(tab => tab.parent === parentId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tabs]);

  const value = useMemo(() => ({
    registerTab,
    registerTabs,
    unregisterTab,
    getTabsHierarchy,
    getLevel1Tabs,
    getChildren,
    tabs
  }), [registerTab, registerTabs, unregisterTab, getTabsHierarchy, getLevel1Tabs, getChildren, tabs]);

  return (
    <TabRegistryContext.Provider value={value}>
      {children}
    </TabRegistryContext.Provider>
  );
};

export const useTabRegistry = () => {
  const context = useContext(TabRegistryContext);
  if (!context) {
    throw new Error('useTabRegistry must be used within a TabRegistryProvider');
  }
  return context;
};

export default TabRegistryContext;





